import React from 'react';
import CustomInput from 'components/common/CustomInput/CustomInput';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupHeading } from 'utils/groupLabel';
import styles from '../VolunteerHoursTargetSettings.module.css';

const AgeBasedConfig = ({ ageGroups, ageHoursConfig, onChange }) => {
  // Each row is one group number, labelled "U11", or "Y6" for a club that groups by year level. The hours
  // are stored against the group number either way.
  const { groupingBasis, phrases } = useGroupLabels();
  const handleHoursChange = (age, value) => {
    const newConfig = {
      ...ageHoursConfig,
      [age]: value === '' ? '' : parseFloat(value) || 0,
    };
    onChange(newConfig);
  };

  // Extract unique ages from age groups
  const getAgesFromGroups = () => {
    const ages = new Set();
    ageGroups.forEach((group) => {
      // Parse ageRange like "U5", "U6", "U7", "U13-17" to extract individual ages
      const range = group.ageRange;
      
      // Remove "U" prefix if present
      const cleanRange = range.replace(/^U/, '');
      
      if (cleanRange.includes('-')) {
        const [start, end] = cleanRange.split('-').map((s) => parseInt(s.trim()));
        for (let age = start; age <= end; age++) {
          ages.add(age);
        }
      } else {
        const age = parseInt(cleanRange);
        if (!isNaN(age)) {
          ages.add(age);
        }
      }
    });
    return Array.from(ages).sort((a, b) => a - b);
  };

  const ages = getAgesFromGroups();

  // Calculate example total
  const calculateExample = () => {
    if (ages.length < 3) return null;
    const exampleAge1 = ages[1];
    const exampleAge2 = ages.length > 2 ? ages[2] : ages[1];
    const hours1 = ageHoursConfig[exampleAge1] || 0;
    const hours2 = ageHoursConfig[exampleAge2] || 0;
    const total = hours1 + hours2;
    return { exampleAge1, exampleAge2, hours1, hours2, total };
  };

  const example = calculateExample();

  return (
    <div className={styles.configSection}>
      <p className={styles.configDescription}>
        {phrases.hoursPerAthlete} A member&apos;s total requirement is the sum of hours
        for all their athletes.
      </p>

      <div className={styles.ageHoursGrid}>
        {ages.map((age) => (
          <div key={age} className={styles.ageHoursRow}>
            <CustomInput
              label={formatGroupHeading(groupingBasis, age)}
              type="number"
              min="0"
              step="0.5"
              value={ageHoursConfig[age] ?? ''}
              onChange={(e) => handleHoursChange(age, e.target.value)}
              placeholder="0.0"
            />
          </div>
        ))}
      </div>

      {example && (
        <div className={styles.exampleBox}>
          <strong>Example:</strong> A member with two athletes ({formatGroupHeading(groupingBasis, example.exampleAge1)}, {formatGroupHeading(groupingBasis, example.exampleAge2)}) 
          would have: {example.hours1} + {example.hours2} = <strong>{example.total} required hours</strong>
        </div>
      )}
    </div>
  );
};

export default AgeBasedConfig;
